import { usePublishedHero } from 'hooks/usePublishedHero';
import { FlowButton } from 'components/ui/flow-button';
import { useEffect, useRef, useState } from 'react';

import { AlertTriangle, BookOpen, CheckCircle2, ClipboardCheck, FileCheck2, FileText, Pause, Play, ShieldCheck, Truck, Volume2, VolumeX, Workflow } from 'lucide-react';
import { DEFAULT_FEATURED_WEBINAR_CONTENT, getPublicFeaturedWebinarContent } from 'lib/webinarCms';


const HERO_POINTS = [
  'Organize compliance before the audit pressure hits.',
  'Make driver onboarding repeatable instead of person-dependent.',
  'Build the operating proof needed for premium freight conversations.',
];

const PROBLEM_POINTS = [
  {
    title: 'Driver files become a mess',
    copy: 'They are hard to review, missing key documents, and leave you exposed when someone finally asks for proof.',
  },
  {
    title: 'Safety follow-up happens too late',
    copy: 'Incidents slip through the cracks or disappear entirely until an audit, insurance review, or customer issue brings them back.',
  },
  {
    title: 'Training depends on who is in the office',
    copy: 'The process is inconsistent, undocumented, and almost impossible to repeat perfectly across shifts and locations.',
  },
  {
    title: 'Recruiting turns into a scramble',
    copy: 'There is no real pipeline, just urgent pressure to fill empty seats after the need is already expensive.',
  },
  {
    title: 'TMS usage is uneven',
    copy: 'Your workflows do not reflect the real operation, and half the team is not using the system the same way.',
  },
  {
    title: 'Management visibility is reactive',
    copy: 'You see problems only after they become urgent, expensive, and difficult to clean up.',
  },
];

const PILLARS = [
  {
    icon: Truck,
    title: 'Premium Freight Readiness',
    copy: 'For carriers pursuing high-value, pharma, cold-chain, government, or shipper-audited freight, we build the paperwork systems needed to prove the operation behind the load: ISO-style QMS / IMS documentation, CAPA templates, and chain-of-custody controls.',
  },
  {
    icon: FileCheck2,
    title: 'Bulletproof Compliance & Driver Files',
    copy: 'Structure document control, training records, acknowledgments, and review rhythm so every file can be found, checked, and explained without a last-minute scramble.',
  },
  {
    icon: ShieldCheck,
    title: 'Ironclad Safety & Incident Tracking',
    copy: 'Build follow-up systems for deviations, corrective actions, incidents, and ownership so safety work does not disappear when the company gets busy.',
  },
  {
    icon: Workflow,
    title: 'Streamlined TMS Workflows',
    copy: 'Align the Transportation Management System and internal handoffs to your real operations, so dispatch, safety, recruiting, accounting, and management work from the same playbook.',
  },
];

const OFFERS = [
  {
    label: 'Option A',
    title: 'The Company Operating Review',
    copy: 'A focused 1-on-1 session to map out your current bottlenecks. We look at safety follow-up, premium freight readiness, driver files, and the first operating gap that needs attention.',
    path: '/companies/apply',
    cta: 'Book your live review session',
  },
  {
    label: 'Option B',
    title: 'The Operations Masterclass',
    copy: 'A focused 10-session training curriculum built around real carrier pressure. Learn the paperwork, safety, training, recruiting, and workflow strategies that solve the problems that keep coming back.',
    path: '/webinars',
    cta: 'Register for the masterclass course',
  },
];

const FAQS = [
  [
    'What does Magellan actually do?',
    'We help trucking companies build operating systems around the work behind the freight: driver files, compliance records, safety follow-up, onboarding, training, recruiting, TMS workflows, SOPs, and management review.',
  ],
  [
    'Is this only for large fleets?',
    'No. Small and mid-sized carriers often need structure earlier because one missing file, weak onboarding process, safety issue, failed audit, or insurance problem can create serious damage to a growing business.',
  ],
  [
    'Is this legal or regulatory advice?',
    'No. Magellan provides operational structure, documentation frameworks, workflow design, training material, and implementation support. Legal, regulatory, insurance, and compliance decisions should be reviewed with qualified professionals.',
  ],
];

function sanitizeText(value) {
  return String(value || '').trim();
}

function parsePublicYouTubeVideoId(value) {
  const candidate = sanitizeText(value);
  if (/^[A-Za-z0-9_-]{11}$/.test(candidate)) return candidate;

  try {
    const url = new URL(candidate);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0] || '';
      return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : '';
    }
    if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'm.youtube.com') {
      const watchId = url.searchParams.get('v') || '';
      if (/^[A-Za-z0-9_-]{11}$/.test(watchId)) return watchId;
      const parts = url.pathname.split('/').filter(Boolean);
      const markerIndex = parts.findIndex((part) => ['embed', 'shorts', 'live'].includes(part));
      const id = markerIndex >= 0 ? parts[markerIndex + 1] || '' : '';
      return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : '';
    }
  } catch {
    return '';
  }

  return '';
}

function buildYouTubePlayerUrl(value, shouldAutoplay) {
  const videoId = parsePublicYouTubeVideoId(value);
  if (!videoId) return '';
  const params = new URLSearchParams({
    autoplay: shouldAutoplay ? '1' : '0',
    controls: '0',
    disablekb: '1',
    enablejsapi: '1',
    fs: '1',
    modestbranding: '1',
    playsinline: '1',
    rel: '0',
  });
  if (typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
  }
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

function postYouTubeCommand(iframe, playerUrl, command, args = []) {
  if (!iframe?.contentWindow || !command) return;
  try {
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: command, args }),
      new URL(playerUrl).origin
    );
  } catch {
    iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: command, args }), '*');
  }
}

function WelcomeVideoPlayer({ src = '', posterUrl = '', title = 'Magellan welcome video' }) {
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const cleanSrc = sanitizeText(src);
  const youtubePlayerUrl = buildYouTubePlayerUrl(cleanSrc, false);
  const isYouTube = Boolean(youtubePlayerUrl);
  const [isStarted, setIsStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => () => {
    videoRef.current?.pause?.();
    postYouTubeCommand(iframeRef.current, youtubePlayerUrl, 'pauseVideo');
  }, [youtubePlayerUrl]);

  useEffect(() => {
    if (isYouTube) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    const syncPlaying = () => setIsPlaying(!video.paused && !video.ended);
    const syncMuted = () => setIsMuted(Boolean(video.muted));

    video.addEventListener('play', syncPlaying);
    video.addEventListener('pause', syncPlaying);
    video.addEventListener('ended', syncPlaying);
    video.addEventListener('volumechange', syncMuted);
    syncPlaying();
    syncMuted();

    return () => {
      video.removeEventListener('play', syncPlaying);
      video.removeEventListener('pause', syncPlaying);
      video.removeEventListener('ended', syncPlaying);
      video.removeEventListener('volumechange', syncMuted);
    };
  }, [cleanSrc, isYouTube]);

  const handlePlayPause = () => {
    if (!cleanSrc) return;

    if (isYouTube) {
      if (!isStarted) {
        setIsStarted(true);
        setIsPlaying(true);
        window.setTimeout(() => postYouTubeCommand(iframeRef.current, youtubePlayerUrl, 'playVideo'), 350);
        return;
      }
      const nextPlaying = !isPlaying;
      postYouTubeCommand(iframeRef.current, youtubePlayerUrl, nextPlaying ? 'playVideo' : 'pauseVideo');
      setIsPlaying(nextPlaying);
      return;
    }

    const video = videoRef.current;
    if (!video) return;
    if (video.paused || video.ended) {
      video.play().catch(() => setIsPlaying(false));
      return;
    }
    video.pause();
  };

  const handleMuteToggle = () => {
    if (isYouTube) {
      const nextMuted = !isMuted;
      postYouTubeCommand(iframeRef.current, youtubePlayerUrl, nextMuted ? 'mute' : 'unMute');
      setIsMuted(nextMuted);
      return;
    }

    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    <div data-theme-surface="dark" className="relative aspect-video w-full overflow-hidden border border-gold/24 bg-black shadow-[0_30px_90px_rgba(0,0,0,0.42)]">
      {isYouTube && isStarted ? (
        <iframe
          ref={iframeRef}
          className="absolute inset-0 h-full w-full bg-black"
          src={youtubePlayerUrl}
          title={title}
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      ) : isYouTube ? (
        <img src={posterUrl || undefined} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" loading="eager" />
      ) : (
        <video
          ref={videoRef}
          data-welcome-video="true"
          className="absolute inset-0 h-full w-full bg-black object-contain"
          preload="metadata"
          playsInline
          poster={posterUrl || undefined}
          src={cleanSrc || undefined}
          title={title}
          controls={false}
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          disableRemotePlayback
          onContextMenu={(event) => event.preventDefault()}
        />
      )}

      {!cleanSrc ? (
        <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-gold/[0.16] via-canvas to-black px-6 text-center">
          <p className="max-w-md font-body text-sm leading-relaxed text-copy/62">
            The welcome video is being prepared. Use the training path below to continue.
          </p>
        </div>
      ) : null}

      <div className="absolute left-4 top-4 border border-ink/14 bg-canvas/78 px-3 py-1 backdrop-blur">
        <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent-ink">10-minute welcome video</p>
      </div>

      {cleanSrc && !isPlaying ? (
        <button
          type="button"
          onClick={handlePlayPause}
          className="absolute left-1/2 top-1/2 inline-flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold/40 bg-gold text-obsidian shadow-[0_0_34px_rgba(76,141,204,0.24)] transition-transform hover:scale-105"
          aria-label="Play welcome video"
        >
          <Play className="h-6 w-6 fill-current" />
        </button>
      ) : null}

      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={handlePlayPause}
          disabled={!cleanSrc}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 bg-canvas/82 text-accent-ink backdrop-blur transition-colors hover:bg-gold hover:text-obsidian disabled:cursor-not-allowed disabled:opacity-45"
          aria-label={isPlaying ? 'Pause welcome video' : 'Play welcome video'}
        >
          {isPlaying ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
        </button>
        <button
          type="button"
          onClick={handleMuteToggle}
          disabled={!cleanSrc || (isYouTube && !isStarted)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 bg-canvas/82 text-copy/74 backdrop-blur transition-colors hover:border-gold/50 hover:text-accent-ink disabled:cursor-not-allowed disabled:opacity-45"
          aria-label={isMuted ? 'Unmute welcome video' : 'Mute welcome video'}
        >
          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function WelcomePage() {
  const heroMedia = usePublishedHero('welcomeHero');
  const promotionPoster = usePublishedHero('coursePromotionPoster');
  const [featuredContent, setFeaturedContent] = useState(DEFAULT_FEATURED_WEBINAR_CONTENT);

  useEffect(() => {
    let mounted = true;
    getPublicFeaturedWebinarContent().then((content) => {
      if (mounted) setFeaturedContent(content);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const videoUrl = sanitizeText(featuredContent.fullOverviewVideoUrl || featuredContent.shortOverviewVideoUrl);
  const posterUrl = promotionPoster.imageUrl;
  const featuredTitle = sanitizeText(featuredContent.title) || '10-session Operations Masterclass';

  return (
    <main className="min-h-screen bg-canvas text-ink">
      <section data-theme-surface="dark" className="relative overflow-hidden border-b border-ink/10 bg-canvas pt-28 pb-16 md:pt-32 md:pb-20">
        <div aria-hidden="true" className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center opacity-28" style={heroMedia.imageUrl ? { backgroundImage: `url(${heroMedia.imageUrl})` } : undefined} />
          <div className="absolute inset-0 bg-gradient-to-b from-canvas via-canvas/92 to-canvas" />
          <div className="absolute inset-0 grain-overlay" />
        </div>

        <div className="site-content-shell relative">
          <div className="mx-auto max-w-5xl text-center">
            <p className="mb-5 font-body text-[10px] font-semibold uppercase tracking-[0.32em] text-accent-ink/85">
              For ambitious trucking owners, dispatchers, and safety directors
            </p>
            <h1 className="font-display text-4xl leading-[0.98] tracking-tight text-ink sm:text-6xl lg:text-7xl">
              Stop Running Your Carrier on Memory, Pressure, and Last-Minute Fixes.
            </h1>
            <p className="mx-auto mt-6 max-w-3xl font-body text-base leading-8 text-copy/58 md:text-lg">
              Build the rock-solid operational systems you need to organize compliance, automate driver onboarding, and finally unlock high-paying premium freight without the daily chaos.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <FlowButton to="/companies/apply" className="inline-flex items-center justify-center gap-2 border border-ink/18 px-7 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-copy/74 transition-all hover:border-gold/55 ">
                Reserve your company review
              </FlowButton>
              <FlowButton as="a" href="#welcome-video" className="inline-flex items-center justify-center gap-2  px-7 py-4 font-body text-[11px] font-bold uppercase tracking-[0.22em]  transition-all ">
                Watch the video <Play className="h-4 w-4 fill-current" />
              </FlowButton>
            </div>
          </div>

          <div id="welcome-video" className="mx-auto mt-12 w-full scroll-mt-28">
            <WelcomeVideoPlayer
              src={videoUrl}
              posterUrl={posterUrl}
              title="Magellan welcome video"
            />
          </div>

          <div className="mx-auto mt-8 grid w-full grid-cols-1 border-y border-ink/10 sm:grid-cols-3">
            {HERO_POINTS.map((point, index) => (
              <div key={point} className={`px-5 py-5 ${index > 0 ? 'sm:border-l sm:border-ink/10' : ''}`}>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-accent-ink" strokeWidth={1.5} />
                  <p className="font-body text-sm leading-7 text-copy/66">{point}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface py-16 md:py-24">
        <div className="site-content-shell grid grid-cols-1 gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
          <div>
            <p className="mb-4 font-body text-[10px] uppercase tracking-[0.3em] text-accent-ink">The problem</p>
            <h2 className="font-display text-3xl leading-tight tracking-tight text-ink md:text-5xl">
              A Carrier Can Be Busy and Still Be Incredibly Hard to Manage.
            </h2>
            <p className="mt-6 font-body text-base leading-8 text-copy/58">
              Loads are moving. Drivers are dispatched. Invoices are going out, and customers are being served. On the outside, your trucking company looks like a well-oiled machine.
            </p>
            <p className="mt-4 font-body text-base leading-8 text-copy/58">
              But behind the scenes, the company is held together by sheer willpower. As you grow, the pressure points start to crack.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden border border-ink/10 bg-ink/10 sm:grid-cols-2">
            {PROBLEM_POINTS.map((item) => (
              <article key={item.title} className="bg-canvas p-6 md:p-7">
                <AlertTriangle className="mb-5 h-5 w-5 text-accent-ink" strokeWidth={1.5} />
                <h3 className="font-display text-2xl leading-tight text-ink">{item.title}</h3>
                <p className="mt-4 font-body text-sm leading-7 text-copy/54">{item.copy}</p>
              </article>
            ))}
          </div>
        </div>
        <div className="site-content-shell mt-12 text-center">
          <p className="border-y border-gold/18 py-6 font-display text-2xl leading-snug text-ink md:text-3xl">
            A carrier does not lose control because people are not working hard. It loses control when the work is not connected, reviewed, and owned.
          </p>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-canvas py-16 md:py-20">
        <div className="site-content-shell grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div data-theme-surface="dark" className="relative min-h-[360px] overflow-hidden border border-gold/16">
            <img src="/images/companies/company-road-truck.png" alt="" className="absolute inset-0 h-full w-full object-cover opacity-72" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/32 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <p className="font-body text-[10px] uppercase tracking-[0.28em] text-accent-ink/90">Paradigm shift</p>
              <p className="mt-3 max-w-md font-display text-3xl leading-tight text-ink">
                Stop managing the chaos. Start scaling the operating system behind the company.
              </p>
            </div>
          </div>
          <div>
            <p className="mb-4 font-body text-[10px] uppercase tracking-[0.3em] text-accent-ink">The solution</p>
            <h2 className="font-display text-3xl leading-tight tracking-tight text-ink md:text-5xl">
              Meet Magellan International: The Operating System for Trucking Companies.
            </h2>
            <p className="mt-6 font-body text-base leading-8 text-copy/58">
              We help trucking companies turn repeated, daily pressure into clear operational structures, clean follow-up, and workflows your team can actually repeat.
            </p>
            <p className="mt-4 font-body text-base leading-8 text-copy/58">
              Instead of putting out fires, we help you build the system behind your trucking company, so the work is visible, reviewed, owned, and easier to scale.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <FlowButton to="/companies/apply" className="inline-flex items-center justify-center gap-2  px-7 py-4 font-body text-[11px] font-bold uppercase tracking-[0.22em]  transition-all ">
                Reserve your company review
              </FlowButton>
              <FlowButton as="a" href="#core-pillars" className="inline-flex items-center justify-center gap-2 border border-ink/18 px-7 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-copy/74 transition-all hover:border-gold/55 ">
                See what we control
              </FlowButton>
            </div>
          </div>
        </div>
      </section>

      <section id="core-pillars" className="border-b border-ink/10 bg-surface py-16 md:py-24">
        <div className="site-content-shell">
          <div className="max-w-4xl">
            <p className="mb-4 font-body text-[10px] uppercase tracking-[0.3em] text-accent-ink">What they get</p>
            <h2 className="font-display text-3xl leading-tight tracking-tight text-ink md:text-5xl">
              What We Bring Under Control
            </h2>
            <p className="mt-5 max-w-3xl font-body text-base leading-8 text-copy/56">
              These are the operating pillars that turn a busy carrier into a company with proof, ownership, follow-up, and a repeatable way to run the work behind the freight.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden border border-gold/16 bg-gold/16 md:grid-cols-2">
            {PILLARS.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <article key={pillar.title} className="bg-canvas p-6 md:p-8">
                  <div className="mb-8 flex items-center justify-between gap-4">
                    <Icon className="h-7 w-7 text-accent-ink" strokeWidth={1.5} />
                    <span className="font-display text-5xl leading-none text-ink/[0.06]">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="font-display text-2xl leading-tight text-ink">{pillar.title}</h3>
                  <p className="mt-4 font-body text-sm leading-7 text-copy/54">{pillar.copy}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-canvas py-16 md:py-24">
        <div className="site-content-shell grid grid-cols-1 gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-center">
          <div className="border border-gold/18 bg-surface p-8 text-center md:p-10">
            <div className="mx-auto flex aspect-square max-w-[260px] items-center justify-center border border-gold/20 bg-black/35 p-3">
              <img src="/images/brand/magellan-compass-mark.png" alt="Magellan compass mark" className="h-full w-full object-contain drop-shadow-[0_0_22px_rgba(76,141,204,0.18)]" loading="eager" />
            </div>
            <p className="mt-7 font-display text-3xl leading-tight text-ink">Ivan Radosavljevic</p>
            <p className="mt-2 font-body text-[10px] uppercase tracking-[0.26em] text-accent-ink/82">
              Founder & CEO | Operations and QA Systems
            </p>
          </div>

          <div>
            <p className="mb-4 font-body text-[10px] uppercase tracking-[0.3em] text-accent-ink">Authority & proof</p>
            <h2 className="font-display text-3xl leading-tight tracking-tight text-ink md:text-5xl">
              Led By the People Behind the Operating Work.
            </h2>
            <blockquote className="mt-8 border-l border-gold/55 pl-6 font-display text-2xl leading-snug text-ink md:text-3xl">
              "A carrier does not lose control because people are not working hard. It loses control when the work is not connected, reviewed, and owned."
            </blockquote>
            <p className="mt-7 font-body text-base leading-8 text-copy/58">
              With deep experience designing systems that support high-stakes logistics, infrastructure, and transportation operations, Ivan and the Magellan team provide practical, in-the-trenches training and systems design for owners, safety teams, and operations managers.
            </p>
            <p className="mt-4 font-body text-base leading-8 text-copy/58">
              We do not just give you theory. We give you the frameworks needed to keep everything moving: records, ownership, corrective action, training rhythm, and management visibility.
            </p>
          </div>
        </div>
      </section>

      <section id="training-options" className="border-b border-ink/10 bg-surface py-16 md:py-24">
        <div className="site-content-shell">
          <div className="max-w-4xl">
            <p className="mb-4 font-body text-[10px] uppercase tracking-[0.3em] text-accent-ink">The offer</p>
            <h2 className="font-display text-3xl leading-tight tracking-tight text-ink md:text-5xl">
              Choose the Pathway That Matches Your Pressure Point.
            </h2>
            <p className="mt-5 max-w-3xl font-body text-base leading-8 text-copy/56">
              Whether you need to fix a specific problem today or overhaul your entire operation, start with the path that fits the pressure inside the company.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {OFFERS.map((offer) => (
              <article key={offer.title} className="border border-gold/16 bg-canvas p-6 md:p-8">
                <p className="font-body text-[10px] uppercase tracking-[0.26em] text-accent-ink/75">{offer.label}</p>
                <h3 className="mt-5 font-display text-3xl leading-tight text-ink">{offer.title}</h3>
                <p className="mt-5 font-body text-sm leading-7 text-copy/56 md:text-base md:leading-8">{offer.copy}</p>
                {offer.path === '/webinars' ? (
                  <div className="mt-6 flex items-start gap-3 border-y border-ink/10 py-4">
                    <BookOpen className="mt-1 h-5 w-5 flex-shrink-0 text-accent-ink" strokeWidth={1.5} />
                    <p className="font-body text-sm leading-7 text-copy/56">
                      Current training path: <span className="text-ink">{featuredTitle}</span>
                    </p>
                  </div>
                ) : (
                  <div className="mt-6 flex items-start gap-3 border-y border-ink/10 py-4">
                    <ClipboardCheck className="mt-1 h-5 w-5 flex-shrink-0 text-accent-ink" strokeWidth={1.5} />
                    <p className="font-body text-sm leading-7 text-copy/56">
                      Best when you want the first bottleneck mapped against your actual records, people, and workflows.
                    </p>
                  </div>
                )}
                <FlowButton to={offer.path} className="mt-7 inline-flex items-center justify-center gap-2  px-7 py-4 font-body text-[11px] font-bold uppercase tracking-[0.22em]  transition-all ">
                  {offer.cta}
                </FlowButton>
              </article>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-5 border border-ink/10 bg-canvas p-6 md:flex-row md:items-center md:justify-between md:p-8">
            <div className="flex max-w-3xl items-start gap-4">
              <FileText className="mt-1 h-6 w-6 flex-shrink-0 text-accent-ink" strokeWidth={1.5} />
              <div>
                <h3 className="font-display text-2xl text-ink">Need the self-study route first?</h3>
                <p className="mt-3 font-body text-sm leading-7 text-copy/54">
                  Start with Magellan resources and operating guides before moving into a review or full training path.
                </p>
              </div>
            </div>
            <FlowButton to="/resources" className="inline-flex items-center justify-center gap-2 border border-ink/18 px-6 py-3 font-body text-[11px] uppercase tracking-[0.2em] text-copy/74 transition-all hover:border-gold/55 ">
              View resources
            </FlowButton>
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-canvas py-16 md:py-24">
        <div className="site-content-shell grid grid-cols-1 gap-10 lg:grid-cols-[0.72fr_1.28fr]">
          <div>
            <p className="mb-4 font-body text-[10px] uppercase tracking-[0.3em] text-accent-ink">Frequently asked questions</p>
            <h2 className="font-display text-3xl leading-tight tracking-tight text-ink md:text-5xl">
              Clear answers before the next step.
            </h2>
          </div>
          <div className="border-y border-ink/10">
            {FAQS.map(([question, answer], index) => (
              <div key={question} className={`py-6 ${index > 0 ? 'border-t border-ink/10' : ''}`}>
                <h3 className="font-display text-2xl leading-tight text-ink">{question}</h3>
                <p className="mt-3 font-body text-sm leading-7 text-copy/56 md:text-base md:leading-8">{answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-theme-surface="dark" className="relative overflow-hidden py-20 md:py-24">
        <div aria-hidden="true" className="absolute inset-0 bg-cover bg-center opacity-34" style={{ backgroundImage: 'url(/images/resources/resources-road-truck.png)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-canvas via-canvas/92 to-canvas/70" />
        <div className="site-content-shell relative z-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <p className="mb-4 font-body text-[10px] uppercase tracking-[0.3em] text-accent-ink">The final push</p>
            <h2 className="max-w-3xl font-display text-3xl leading-tight tracking-tight text-ink md:text-5xl">
              The Freight Moves on the Road. Make Sure the Company Holds Together Behind It.
            </h2>
            <p className="mt-5 max-w-2xl font-body text-base leading-8 text-copy/58">
              Do not let another week pass running on scattered files and undocumented follow-up. Build the part of the company that keeps everything else moving.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <FlowButton to="/companies/apply" className="inline-flex items-center justify-center gap-2  px-7 py-4 font-body text-[11px] font-bold uppercase tracking-[0.22em]  transition-all ">
              Start your company review today
            </FlowButton>
            <FlowButton as="a" href="#welcome-video" className="inline-flex items-center justify-center gap-2 border border-ink/18 px-7 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-copy/74 transition-all hover:border-gold/55 ">
              Rewatch the video <Play className="h-4 w-4 fill-current" />
            </FlowButton>
          </div>
        </div>
      </section>
    </main>
  );
}
